import type { AudioRhythmMode, EffectRenderer, Rgb } from '../types'
import { scaleRgb } from '../color'

function emptyFrame(): Map<number, Rgb> {
  return new Map()
}

/** 彩虹等渐变统一按水平位置采样（从左到右）。 */
function colorByX(sampleColor: (t: number) => Rgb, nx: number): Rgb {
  return sampleColor(Math.max(0, Math.min(1, nx)))
}

/** 频谱柱：每列高度由频谱驱动，从底向上逐行点亮。 */
const spectrumBar: EffectRenderer = ({ keys, bands, sampleColor, columnCount, rowCount }) => {
  const frame = emptyFrame()
  const rows = Math.max(rowCount, 1)
  for (const key of keys) {
    const col = Math.min(columnCount - 1, Math.floor(key.nx * columnCount))
    const level = bands.columns[col] ?? 0
    // 自下而上：level=0 全灭；level=1 点亮全部行。row 0 在顶部。
    const litRows = Math.ceil(level * rows)
    if (litRows <= 0) continue
    const fromBottom = rows - 1 - key.row // 0=底行 … rows-1=顶行
    if (fromBottom >= litRows) continue

    // 底部更亮，柱顶略亮作为峰值提示
    const isPeak = fromBottom === litRows - 1
    const base = 0.35 + level * 0.45
    const intensity = Math.min(1, isPeak ? base + 0.35 + bands.high * 0.15 : base)
    frame.set(key.ledIndex, scaleRgb(colorByX(sampleColor, key.nx), intensity))
  }
  return frame
}

/** 中央脉冲相位（累积）：pulseFreq 随 mid 变化，用「绝对时间×瞬时频率」会相位跳变（抽搐），
 *  改为 ∫2πf dt 累积，频率变化时相位连续。 */
let pulsePhase = 0
let lastPulseTimeSec: number | null = null

/**
 * 中央脉冲：圆心点 + 圆形脉冲——
 * 圆从中心点快速膨胀再收缩（心跳式），按键盘宽高比校正为视觉正圆，
 * 最大直径不超过键盘高度（完整可见、不充满全键盘）；
 * 鼓点/低频触发瞬爆，脉冲频率随音乐节奏加 快。
 */
const centralPulse: EffectRenderer = ({ keys, bands, sampleColor, rowCount, columnCount }) => {
  const frame = emptyFrame()
  // 视觉圆校正：键盘宽 > 高（列数>行数），ny 距离按 行/列 比例缩小，
  // 使归一化距离在视觉上等距，避免圆被横向拉成扁椭圆。
  const aspect = Math.max(0.2, Math.min(1, (rowCount || 6) / (columnCount || 15)))
  // 脉冲频率：基础约 1.3Hz，随中频能量加快（更活泼）
  const pulseFreq = 1.3 + bands.mid * 1.7
  if (lastPulseTimeSec == null) lastPulseTimeSec = bands.timeSec
  const dt = Math.max(0, Math.min(0.1, bands.timeSec - lastPulseTimeSec))
  lastPulseTimeSec = bands.timeSec
  pulsePhase += 2 * Math.PI * pulseFreq * dt
  const rawPulse = 0.5 + 0.5 * Math.sin(pulsePhase)
  // 非对称脉冲：收缩期贴近 0、膨胀短促集中 → 更有冲击感（像心跳“砰”）
  const pulse = Math.pow(rawPulse, 1.6)
  // 鼓点/低频调制：强时圆冲得更大
  const kick = Math.min(1, bands.low * 0.7 + bands.beat * 0.9)
  // 半径（校正坐标）：最小 0.05（圆心点），脉冲峰值膨胀到约 0.42~0.47，
  // 视觉上 nx 方向直径约 84%~94% 键盘宽（几乎占满键盘），纵向超出被键盘边界裁剪
  const radius = 0.05 + pulse * 0.37 + kick * 0.05

  for (const key of keys) {
    const d = Math.hypot(key.nx - 0.5, (key.ny - 0.5) * aspect)
    // 圆盘：中心最亮、向边缘平滑渐暗（无环状区），边缘柔和收尾
    const disc = d <= radius ? Math.pow(Math.max(0, 1 - d / Math.max(0.01, radius)), 1.3) : 0
    if (disc < 0.05) continue
    // 颜色从中心点向外径向渐变（同节拍呼吸），而非按水平位置左右渐变
    const t = Math.min(1, d / 0.54)
    frame.set(key.ledIndex, scaleRgb(colorByX(sampleColor, t), disc))
  }
  return frame
}

/** 峰值仪滚动频谱历史：最近 N 帧频谱快照（每帧 rows 个频率档）。 */
const peakFrames: number[][] = []
let peakDisplay: number[] = []
let peakScrollAcc = 0 // 亚列滚动累计（0~1），每帧滚半列
const PEAK_FRAME_COUNT = 32 // 保留 32 帧历史

/**
 * 峰值仪：滚动频谱瀑布——显示频谱「变化量 + 峰值保持」，
 * 从右往左连续滚动；持续低频不常亮，只有突发的峰亮起后缓慢衰减，
 * 峰的位置随音乐在频率轴（底=低频、顶=高频）移动，峰从底部低频升起。
 */
const peakMeter: EffectRenderer = ({ keys, bands, sampleColor, columnCount, rowCount }) => {
  const frame = emptyFrame()
  const cols = Math.max(1, columnCount)
  const rows = Math.max(1, rowCount)

  // 当前频谱 → rows 个频率档：每档取该频段内列的最大值
  const freqBins = new Array<number>(rows).fill(0)
  for (let c = 0; c < cols; c++) {
    const bin = Math.min(rows - 1, Math.floor((c / cols) * rows))
    const v = bands.columns[c] ?? 0
    if (v > freqBins[bin]) freqBins[bin] = v
  }

  // 归一化 + 幂次增强：峰=满亮，幂次更高让非峰档快速压暗（峰形陡峭、边界清晰）
  const maxV = Math.max(0.05, ...freqBins)
  const peakVal = freqBins.map((v) => Math.pow(Math.min(1, v / maxV), 2.5))
  // 峰值保持：上升立即、下降缓慢衰减
  if (peakDisplay.length !== rows) peakDisplay = new Array<number>(rows).fill(0)
  for (let b = 0; b < rows; b++) {
    peakDisplay[b] = Math.max(peakVal[b], peakDisplay[b] * 0.85)
  }
  // 阈值硬切：峰与背景之间明显分割（无渐变过渡），阈值提高让峰更尖、谷底更暗
  const sharp = peakDisplay.map((v) => (v > 0.3 ? v : 0))

  // 亚列滚动：每帧滚半列，满一列才推入新快照（降速、减少每帧变化，缓解真机分段撕裂）
  peakScrollAcc += 0.5
  if (peakScrollAcc >= 1 || peakFrames.length === 0) {
    peakScrollAcc = 0
    peakFrames.push(sharp)
    if (peakFrames.length > PEAK_FRAME_COUNT) peakFrames.shift()
  }

  const len = peakFrames.length
  for (const key of keys) {
    const c = Math.min(cols - 1, Math.floor(key.nx * cols)) // 列：0=左（旧）… 最右=最新
    // 亚列插值：帧位置（浮点）= 最新帧往回 (cols-1-c) 列，再减累计滚动偏移
    const pos = len - 1 - (cols - 1 - c) - peakScrollAcc
    const i0 = Math.floor(pos)
    const frac = pos - i0
    const r = rows - 1 - key.row // 底部=低频、顶部=高频（峰从底部低频升起）
    const v0 = i0 >= 0 && i0 < len ? (peakFrames[i0][r] ?? 0) : 0
    const v1 = i0 + 1 >= 0 && i0 + 1 < len ? (peakFrames[i0 + 1][r] ?? 0) : 0
    const level = v0 * (1 - frac) + v1 * frac
    // 新数据更亮、旧数据渐暗（向左淡出）
    const age = (cols - 1 - c) / Math.max(1, cols - 1)
    const intensity = level * (1 - age * 0.6)
    if (intensity < 0.03) continue
    // 颜色跟随左右移动轴（水平/时间轴）：滚动瀑布从左往右按时间渐变
    frame.set(key.ledIndex, scaleRgb(colorByX(sampleColor, key.nx), intensity))
  }
  return frame
}

/** 扫描线相位（累积）：频率随 mid 变化时相位连续，避免「绝对时间×瞬时频率」的相位跳变。 */
let scanPhase = 0
/** 上一帧 timeSec，用于计算帧间隔 dt。 */
let lastScanTimeSec: number | null = null

/**
 * 扫描线：一条清晰的高斯光束沿同一方向循环扫动（无拖尾、无回扫）。
 * - 光束用高斯软边，中心亮、两侧柔和渐暗；
 * - 宽度/亮度随能量变化，节拍触发时光束爆亮。
 */
const scanLine: EffectRenderer = ({ keys, bands, sampleColor }) => {
  const frame = emptyFrame()
  const energy = Math.min(1, bands.overall * 0.7 + bands.mid * 0.3)
  // 相位累积：∫2πf dt，频率随 mid 变化时相位连续
  if (lastScanTimeSec == null) lastScanTimeSec = bands.timeSec
  const dt = Math.max(0, Math.min(0.1, bands.timeSec - lastScanTimeSec))
  lastScanTimeSec = bands.timeSec
  // 扫描速度 = 每帧位移×设备帧率。真机帧率受设备 0xdd 吞吐限制（常 15~30fps），
  // 波束又窄（≤2 排），每帧位移过大时表现为可见跳步；
  // 基频 0.9→0.65、mid 增益 1.3→0.9：同帧率下每帧位移减小约 30%，跳步随之减弱。
  scanPhase += 2 * Math.PI * (0.65 + bands.mid * 0.9) * dt
  // 光束中心：单向扫描（同一方向一直扫）——相位取模 0~2π，x 从 0 线性扫到 1 后跳回 0 重新扫
  const x = (scanPhase % (2 * Math.PI)) / (2 * Math.PI)

  // 光束剖面：超高斯（k=2.5）——中心平坦、翼部陡降。
  // 注意预览与真机的亮度差：真机 sink 会再乘 1.8× 增益，
  // k=2 的翼部（相邻列原始强度 ≈0.04）增益后仍有 17/255，配合背光亮度成可见的 2-3 排；
  // k=2.5 把相邻列压到 ≈0.012（增益后 ≈3.7，不可见），
  // 跨列时两列同时过渡（σ=0.035 ≈ 半列距 0.033，过渡连续不跳动）。
  const sigma = 0.035
  // 彗星形：波束中心左侧（已扫过的尾侧）整体压暗，前缘保持亮度，
  // 让「前一排亮、后一排暗」有方向感（跨列时后一列 ≈35% 亮度）。
  const TRAIL_DIM = 0.35
  // 节拍爆亮增量
  const kick = bands.beat * 0.4
  // 两端淡入淡出：接近左右边界时渐暗，复位（1→0）时柔和淡入淡出，避免瞬间跳变
  const fadeZone = 0.12
  const edge = Math.min(x, 1 - x)
  const edgeFade = Math.min(1, edge / fadeZone)

  for (const key of keys) {
    const dist = Math.abs(key.nx - x)
    // 超高斯光束核心（软边、翼部陡降）
    const r = (dist * dist) / (2 * sigma * sigma)
    let beam = Math.exp(-(r * r * Math.sqrt(r)))
    // 尾侧压暗：nx < x 的键为已扫过的尾侧
    if (key.nx < x) beam *= TRAIL_DIM
    // 合成强度：只有光束、无底光——扫描线之外全灭，只有一条亮线扫过；两端乘 edgeFade 淡入淡出
    const intensity = Math.min(1, beam * (0.5 + energy * 0.6 + kick) * edgeFade)
    if (intensity < 0.04) continue
    frame.set(key.ledIndex, scaleRgb(colorByX(sampleColor, key.nx), intensity))
  }
  return frame
}

/** 波浪相位（累积）：speed 随 mid 变化，用「绝对时间×瞬时频率」会相位跳变（抽搐），
 *  改为 ∫speed dt 累积，频率变化时相位连续。 */
let wavePhase = 0
let lastWaveTimeSec: number | null = null

/** 波浪：细波脊 + 窄高斯软边，兼顾平滑与清晰轮廓。 */
const wave: EffectRenderer = ({ keys, bands, sampleColor }) => {
  const frame = emptyFrame()
  const energy = Math.min(1, bands.overall * 0.7 + bands.mid * 0.35)
  // 波幅更大：波脊上下起伏更明显（曲线/波浪过渡感）
  const amp = 0.2 + energy * 0.24
  const freq = Math.PI * 1.4
  // 波浪上下波动角速度（rad/s），随 mid 加快；
  // 提高到 ~5~8 rad/s（波动周期约 0.8~1.3 秒），让波浪跟上音乐律动，
  // 与扫描线（约 0.45~1.1 秒/圈）等效果节奏基本对齐（旧值 1.1~2.5 偏慢约 5 倍）
  const speed = 5 + bands.mid * 3
  if (lastWaveTimeSec == null) lastWaveTimeSec = bands.timeSec
  const dt = Math.max(0, Math.min(0.1, bands.timeSec - lastWaveTimeSec))
  lastWaveTimeSec = bands.timeSec
  wavePhase += speed * dt
  const phase = wavePhase
  // 波脊稍宽更可见（约 1.5 行），仍保留清晰轮廓
  const sigma = 0.06 + bands.high * 0.02

  // 颜色附着在波脊上：颜色流动与波脊横向移动同步（波脊向左、颜色也向左），
  // 避免颜色用固定速度独立流动时与波脊移动速度（随 mid 变化）不一致，产生「抽搐/追赶」。
  const colorScroll = (((wavePhase / freq) % 1) + 1) % 1

  for (const key of keys) {
    const waveY =
      0.5 +
      Math.sin(key.nx * freq + phase) * amp +
      Math.sin(key.nx * freq * 0.5 + phase * 0.7 + 1.1) * amp * 0.12

    const absDist = Math.abs(key.ny - waveY)
    const ridge = Math.exp(-(absDist * absDist) / (2 * sigma * sigma))
    const intensity = Math.min(1, ridge * (0.55 + energy * 0.45))
    // 更高阈值，去掉过宽的弱光晕
    if (intensity < 0.1) continue

    // 颜色采样位置随 colorScroll 横向漂移，让色带水平流动
    const nxFlow = (((key.nx - colorScroll) % 1) + 1) % 1
    frame.set(key.ledIndex, scaleRgb(colorByX(sampleColor, nxFlow), intensity))
  }
  return frame
}

/** 简易伪噪声，用于火焰湍流（稳定、无分配）。 */
function hashNoise(x: number, y: number, t: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7 + t * 19.3) * 43758.5453
  return n - Math.floor(n)
}

function smoothNoise(x: number, y: number, t: number): number {
  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const fx = x - x0
  const fy = y - y0
  const ux = fx * fx * (3 - 2 * fx)
  const uy = fy * fy * (3 - 2 * fy)
  const a = hashNoise(x0, y0, t)
  const b = hashNoise(x0 + 1, y0, t)
  const c = hashNoise(x0, y0 + 1, t)
  const d = hashNoise(x0 + 1, y0 + 1, t)
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy
}

/** 火焰：从底部中心径向扩散的一团火焰——安静时小火、高能量时几乎占满全屏；
 *  径向渐变（中心热核橙、向外变红），轮廓凹凸如火舌，随音乐跳动。 */
const flame: EffectRenderer = ({ keys, bands, sampleColor, rowCount, columnCount }) => {
  const frame = emptyFrame()
  // 音乐能量：低频主导，节拍权重提高让火焰随鼓点明显「跳高」
  const heat = Math.min(1, bands.low * 0.85 + bands.mid * 0.35 + bands.beat * 0.6)
  const roar = 0.35 + heat * 0.65
  const t = bands.timeSec

  // 宽高比校正：键盘宽 > 高，水平距离放大，让火焰团呈视觉正圆（而非被拉成扁椭圆）
  const aspect = Math.max(1, (columnCount || 15) / Math.max(1, rowCount || 5))

  // 火焰原点在键盘底部中心
  const cx = 0.5
  const cy = 1.0

  // 火焰半径：安静时小火（约 0.5），高能量时几乎占满全屏（到顶部角落约 1.85）
  const flicker = (smoothNoise(1.7, 0.9, t * 3.5) - 0.5) * 0.35 * roar
  const R = Math.max(0.45, 0.5 + heat * 1.35 + flicker)

  for (const key of keys) {
    const dx = (key.nx - cx) * aspect
    const dy = key.ny - cy // 负值=向上
    const d = Math.hypot(dx, dy)

    // 火焰轮廓扰动：随方向与时间凹凸，形成火舌「摇摆」
    const angle = Math.atan2(dy, dx)
    const edgeNoise =
      (smoothNoise(Math.cos(angle) * 2.5, Math.sin(angle) * 2.5, t * 4.5) - 0.5) * 0.3 * roar
    const localR = R + edgeNoise

    if (d > localR) continue

    const rel = d / Math.max(0.3, R) // 0 中心 ~1 边缘

    // 径向渐变：中心亮、向外渐暗；中心热核最亮并随音乐增强
    const body = Math.pow(Math.max(0, 1 - rel), 1.3)
    const core = Math.max(0, 1 - rel / 0.4)

    let intensity = (body * 0.55 + core * 0.5) * (0.45 + roar * 0.55)
    intensity += core * heat * 0.4

    intensity = Math.min(1, intensity)
    if (intensity < 0.05) continue

    // 颜色从中心到边缘：炎心用调色板起点色（彩虹为红、烈焰为橙），
    // 幂次压缩 rel 让中心更贴近起点色，使红色炎心占据更明显的中心区域
    const colorRel = Math.pow(Math.min(1, rel), 1.7)
    const color = colorByX(sampleColor, colorRel)
    frame.set(key.ledIndex, scaleRgb(color, intensity))
  }
  return frame
}

/**
 * 节拍呼吸：只有亮度变化——慢速全盘明暗呼吸，且亮度从中心向两边平滑衰减。
 * 通过指数平滑严格限制每帧亮度变化量：即使设备按段/按区域刷新，
 * 相邻帧（段间）亮度差始终极小，看不到"分片刷灯"。
 */
let breatheEnv = 0.6
/** 呼吸相位（累积）：频率随 mid 变化时相位保持连续，避免相位跳变。 */
let breathePhase = 0
/** 上一帧 timeSec，用于计算帧间隔 dt。 */
let lastBreatheTimeSec: number | null = null
const beatBreathe: EffectRenderer = ({ keys, bands, sampleColor, rowCount, columnCount }) => {
  const frame = emptyFrame()
  // 宽高比校正：让颜色渐变呈视觉圆形（从中心圆点向外）
  const aspect = Math.max(0.2, Math.min(1, (rowCount || 6) / (columnCount || 15)))
  // 呼吸相位用「累积」而非「绝对时间 × 瞬时频率」：后者在 timeSec 变大后，
  // mid 每帧的小幅波动会被 timeSec 放大成巨大相位跳变，把呼吸正弦变成随机噪声、
  // 周期消失、幅度被 env 平滑抹平（即「放久了亮度变化不大」）。
  if (lastBreatheTimeSec == null) lastBreatheTimeSec = bands.timeSec
  const dt = Math.max(0, Math.min(0.1, bands.timeSec - lastBreatheTimeSec))
  lastBreatheTimeSec = bands.timeSec
  breathePhase += 2 * Math.PI * (0.45 + bands.mid * 0.3) * dt
  const cycle = Math.sin(breathePhase)
  // 目标亮度：0.2（暗）~1.0（满亮），节拍触发更亮
  const target = Math.min(1, 0.6 + cycle * 0.4 + bands.beat * 0.15)
  // 快速跟随：高平滑 + 截断（0.08 平衡「对比明显」与「条纹/分片」）
  const delta = (target - breatheEnv) * 0.45
  breatheEnv += Math.max(-0.08, Math.min(0.08, delta))
  const env = breatheEnv
  for (const key of keys) {
    // 颜色径向渐变：从键盘中心圆点向外（径向距离采样，视觉圆形）
    const dx = key.nx - 0.5
    const dy = (key.ny - 0.5) * aspect
    const d = Math.hypot(dx, dy)
    const t = Math.min(1, d / 0.54) // 0=中心圆点 ~1=边缘/角落
    const color = colorByX(sampleColor, t)
    // 明暗全屏均匀一起变（无空间亮度差异）
    const intensity = env
    if (intensity < 0.05) continue
    frame.set(key.ledIndex, scaleRgb(color, intensity))
  }
  return frame
}

export const EFFECT_RENDERERS: Record<AudioRhythmMode, EffectRenderer> = {
  spectrumBar,
  centralPulse,
  peakMeter,
  scanLine,
  wave,
  flame,
  beatBreathe,
}
